/**
 * content/index.js - Door Knock's declaration.
 *
 * Written second, and deliberately written the way a second author writes it:
 * the declaration, and the checks that occurred to me at the time. What it does
 * NOT re-check is everything Bubble Pop's validateContent() checks and I did
 * not think to copy - that list is the point of the exercise and is in
 * docs/audit/GAME-02-CONTRACT.md. The shared checks now live in
 * client/test/contractConformance.test.js and run against both games.
 */

import { validateCapabilities, validateCoverage } from '../../../../engine/framework.js';
import { LEVELS, COVERAGE, getLevelContent, pacingFor } from './levels.js';
import { CREATURES, CARRIERS, PLACES, VERBS } from './creatures.js';
import { PRAISE, ENCOURAGEMENT, NEARLY, ASSIST } from './voice.js';

export const GAME = Object.freeze({
  id: 'door-knock',
  name: 'Door Knock',
  description:
    'A street of doors. Say who is behind one and it opens. Phases 2 to 4: '
    + 'naming with fading support, two-word requests, and early grammar.',
  phasesAvailable: COVERAGE.phasesAvailable,
  levelsAvailable: Object.freeze(COVERAGE.levelsAvailable),
  // Only what it actually instruments. Phase 2 is entirely prompt-sequence
  // dependent, so that one is not optional; audio energy buys nothing here,
  // because the only box needing it is Phase 1 B and this game has no Phase 1.
  capabilities: Object.freeze({ prompt_sequence: true, audio_energy: false }),
  minAge: 2,
  maxAge: 8,
  skillTags: Object.freeze(['speech', 'vocabulary', 'requesting', 'grammar']),
  totalLevels: LEVELS.length,
});

export const POOLS = Object.freeze({
  praise: PRAISE,
  encouragement: ENCOURAGEMENT,
  nearly: NEARLY,
  assist: ASSIST,
});

/** The checks this author thought of. See the header. */
export function validateContent() {
  const errors = [];

  const caps = validateCapabilities(GAME.capabilities);
  if (!caps.ok) errors.push(...caps.errors);

  const coverage = validateCoverage({
    phasesAvailable: GAME.phasesAvailable,
    levelsAvailable: GAME.levelsAvailable,
  });
  if (!coverage.ok) errors.push(...coverage.errors);

  for (const level of LEVELS) {
    if (!level.pool.length) errors.push(`${level.id}: empty pool`);
    for (const id of level.pool) {
      if (!CREATURES[id]) errors.push(`${level.id}: unknown creature "${id}"`);
    }
    for (const id of level.carriers || []) {
      if (!CARRIERS[id]) errors.push(`${level.id}: unknown carrier "${id}"`);
    }
    for (const id of level.places || []) {
      if (!PLACES[id]) errors.push(`${level.id}: unknown place "${id}"`);
    }
    for (const id of level.verbs || []) {
      if (!VERBS[id]) errors.push(`${level.id}: unknown verb "${id}"`);
    }
  }

  return { ok: errors.length === 0, errors, warnings: [] };
}

export { LEVELS, COVERAGE, getLevelContent, pacingFor } from './levels.js';
export * as VOICE from './voice.js';

export default { GAME, POOLS, validateContent };
